import React, { useEffect, useState } from "react";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	IconButton,
	Typography,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	MenuItem,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTranslationContext } from "../../../../hooks/translations/translationsProvider";
import { useUserContext } from "../../../../hooks/auth/UserProvider";
import { Roles } from "../../../../helpers/authorizedUser";
import { getRoles, getUserRoles, assignRole, removeRole } from "../../../../hooks/actions/identityManagement";
import { RoleResponse, UserWithRoles } from "../../../../models/identity/IdentityManagement";
import {
	bodyCellSx,
	headCellSx,
	outlinedButtonSx,
	primaryButtonSx,
	tableContainerSx,
	titleSx,
} from "./identityStyles";

interface UserRolesDetailProps {
	user: UserWithRoles;
	onBack: () => void;
}

const DEV_SUPER_ADMIN = Roles.DevSuperAdmin;

const UserRolesDetail: React.FC<UserRolesDetailProps> = ({ user, onBack }) => {
	const { t } = useTranslationContext();
	const { user: currentUser } = useUserContext();
	const currentUserId = currentUser?.userId;

	const [roles, setRoles] = useState<string[]>(user.roles ?? []);
	const [allRoles, setAllRoles] = useState<RoleResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [addOpen, setAddOpen] = useState(false);
	const [selectedRole, setSelectedRole] = useState("");

	const load = async () => {
		setError(null);
		setLoading(true);
		try {
			const [userRoles, rolesList] = await Promise.all([getUserRoles(user.id), getRoles()]);
			setRoles(userRoles ?? []);
			setAllRoles(rolesList ?? []);
		} catch (e: any) {
			setError(t("Errore nel caricamento dei ruoli"));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.id]);

	const availableRoles = allRoles.map((r) => r.name).filter((name) => !roles.includes(name));

	const handleAdd = async () => {
		if (!selectedRole) return;
		setError(null);
		try {
			const res = await assignRole(user.id, selectedRole);
			if (res?.succeeded) {
				setAddOpen(false);
				setSelectedRole("");
				await load();
			} else {
				setError(res?.error || t("Operazione non riuscita"));
			}
		} catch (e: any) {
			setError(e?.error || t("Operazione non riuscita"));
		}
	};

	const handleRemove = async (roleName: string) => {
		setError(null);
		try {
			const res = await removeRole(user.id, roleName);
			if (res?.succeeded) {
				await load();
			} else {
				setError(res?.error || t("Operazione non riuscita"));
			}
		} catch (e: any) {
			setError(e?.error || t("Operazione non riuscita"));
		}
	};

	const isSelfDevSuperAdmin = (roleName: string) =>
		roleName === DEV_SUPER_ADMIN && user.id === currentUserId;

	return (
		<Box>
			{error && (
				<Typography color="error" sx={{ mb: 2 }}>
					{error}
				</Typography>
			)}

			<Typography variant="h6" sx={titleSx}>{user.email}</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				{user.userName}
			</Typography>

			<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
				<Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} sx={outlinedButtonSx}>
					{t("Indietro")}
				</Button>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => setAddOpen(true)}
					disabled={availableRoles.length === 0}
					sx={primaryButtonSx}
				>
					{t("Assegna ruolo")}
				</Button>
			</Box>

			<TableContainer component={Paper} sx={tableContainerSx}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={headCellSx}>{t("Ruolo")}</TableCell>
							<TableCell sx={headCellSx} align="right">{t("Azioni")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell sx={bodyCellSx} colSpan={2}>{t("Caricamento...")}</TableCell>
							</TableRow>
						) : roles.length === 0 ? (
							<TableRow>
								<TableCell sx={bodyCellSx} colSpan={2}>{t("Nessun ruolo assegnato")}</TableCell>
							</TableRow>
						) : (
							roles.map((roleName) => (
								<TableRow key={roleName} hover>
									<TableCell sx={bodyCellSx}>{roleName}</TableCell>
									<TableCell sx={bodyCellSx} align="right">
										<Tooltip
											title={
												isSelfDevSuperAdmin(roleName)
													? t("Non puoi rimuovere DevSuperAdmin a te stesso")
													: t("Rimuovi ruolo")
											}
										>
											<span>
												<IconButton
													size="small"
													color="error"
													disabled={isSelfDevSuperAdmin(roleName)}
													onClick={() => handleRemove(roleName)}
												>
													<DeleteIcon />
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>{t("Assegna ruolo")}</DialogTitle>
				<DialogContent>
					<TextField
						select
						fullWidth
						margin="dense"
						label={t("Ruolo")}
						value={selectedRole}
						onChange={(e) => setSelectedRole(e.target.value)}
					>
						{availableRoles.map((name) => (
							<MenuItem key={name} value={name}>
								{name}
							</MenuItem>
						))}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddOpen(false)} sx={outlinedButtonSx}>{t("Annulla")}</Button>
					<Button variant="contained" onClick={handleAdd} disabled={!selectedRole} sx={primaryButtonSx}>
						{t("Assegna")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default UserRolesDetail;
